import {
  DataGridPro,
  DataGridProProps,
  GridCellParams,
  GridColDefExtended,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridDensity,
  GridFilterModel,
  gridRowSelectionIdsSelector,
  GridSlots,
  GridSortModel,
  MuiEvent,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useAppSelector } from '@reduxStore';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { getDispatchGridColDefs } from './columns';
import { expandSubColumns } from '../subColumns';
import ColumnsManagement from '../ColumnsManagement';
import { applyHiddenColumns, boardLayoutStorageKey, persistHiddenColumns } from './hiddenColumns';
import { applyColumnWidths, fallbackColumnWidth, persistColumnWidth, resetColumnWidths } from './columnWidths';
import BrandLinearProgress from '@/Infrastructure/Components/Loading/BrandLinearProgress';
import DispatchGridToolbar from './DispatchGridToolbar';
import { convertQuerySortOrderToGridSortModel, defaultFilterModel } from './types';
import useDispatchRows from '@hooks/useDispatchRows';
import useDispatchBoardQueries from '@hooks/useDispatchBoardQueries';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { defaultAllOpenQuery } from '@api/Trinium/Endpoints/BoardQueries/types';
import { useNavigate, useSearchParams } from 'react-router';
import { GenericOverlay } from '../GenericNoRowsOverlay';
import { emptyRowSet } from '../types';
import EditableCellPopover, { EditableCellPopoverRef } from '../EditableCellPopover';
import { AppLocalStorageKeys } from '@/Infrastructure/Configuration/types';
import { DENSITY_OPTIONS } from '../ToolbarItems/DensityMenu';

import styles from '../RenderCell.module.css';

const ROW_CLASS_NAME = styles['custom-row'];
const getRowId = (row: DispatchLeg) => row.legId;
const getRowClassName = () => ROW_CLASS_NAME;

const storedDensity = localStorage.getItem(AppLocalStorageKeys.GridDensity);
const initialDensity: GridDensity = DENSITY_OPTIONS.some(({ value }) => value === (storedDensity as GridDensity))
  ? (storedDensity as GridDensity)
  : 'standard';
const initialGridState = { density: initialDensity };

/**
 * Dispatch Grid component implementing the MUI DataGridPro
 */
export default function Grid() {
  const popoverRef = useRef<EditableCellPopoverRef>(null);
  const [, startTransition] = useTransition();
  const navigate = useNavigate();
  const [searchParameters] = useSearchParams();
  const isJobMode = !!searchParameters.get('job');
  const { selectedBoardQueryName, selectedTerminal, quickFilterLogicOperator } = useAppSelector((state) => state.applicationState);
  const { defaultQuery, currentQuery } = useDispatchBoardQueries(selectedTerminal?.number, selectedBoardQueryName);
  const { dispatchLegs, isLoading } = useDispatchRows(currentQuery);
  const theme = useTheme();

  /** Data Grid API ref for accessing Grid outside the render */
  const gridApiRef = useGridApiRef();

  /** Tracking Data Grid filtering */
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    ...defaultFilterModel,
    quickFilterLogicOperator,
    quickFilterValues: []
  });

  /** Sync quick filter logic operator from Redux into the filter model */
  useEffect(() => {
    setFilterModel((prev) => ({
      ...prev,
      quickFilterLogicOperator
    }));
  }, [quickFilterLogicOperator]);

  /** Using manual sorting model when the user is filtering */
  const [sortingModel, setSortingModel] = useState<GridSortModel>();

  /** Update sorting model when current query changes. In job/consignment view (e.g. "View All Legs for this Container"),
   * clear the sort so the data layer's csn/leg-number ordering from sortJobLegs takes effect rather than retaining the parent query's sort. */
  useEffect(() => {
    if (isJobMode) {
      setSortingModel([]);
    } else if (currentQuery.configuration.sortOrder?.length) {
      const sortModel = convertQuerySortOrderToGridSortModel(currentQuery);
      setSortingModel(sortModel);
    } else {
      setSortingModel([]);
    }
  }, [currentQuery, isJobMode]);

  /**
   * Override for the Filter Model change event to allow us to clear the selected rows when the filter/dataset changes.
   * Memoized because a new handler identity changes the grid's root props, which re-renders every row, cell and header
   * — and Grid re-renders on every search param change (e.g. opening the consignment modal).
   * @param gridFilterModel MUI GridFilterModel
   */
  const changeFilterModel = useCallback(
    (gridFilterModel: GridFilterModel) => {
      gridApiRef.current?.setRowSelectionModel(emptyRowSet);

      startTransition(() => {
        /** When the user is typing in a filter and they are not on the default board query,
         * return them to the default query so that they are filtering against all legs.
         */
        if ((gridFilterModel.quickFilterValues?.length ?? 0) > 0) {
          void navigate(`/${String(selectedTerminal?.number)}/Board/${defaultQuery ? defaultAllOpenQuery.name : ''}`);
          setSortingModel([{ field: 'orderNumber', sort: 'asc' }]);
        } else {
          setSortingModel([]);
        }

        setFilterModel(gridFilterModel);
      });
    },
    [gridApiRef, navigate, selectedTerminal?.number, defaultQuery]
  );

  const changeSortModel = useCallback((gridSortModel: GridSortModel) => {
    setSortingModel(gridSortModel);
  }, []);

  /**
   * Show/Hide Columns and column resizing write here and to local storage rather than to the query, so a
   * dispatcher's own layout survives a reload without editing the shared Board Query. Switching query reseeds
   * from that query's own stored layout, which is why the key carries the query name.
   */
  const storageKey = boardLayoutStorageKey(selectedTerminal?.number, currentQuery.name);

  /**
   * Retrieve the Data Grid columns for the current Board Query, expanded with hidden sub-sort companion columns
   * and overridden with the dispatcher's stored widths.
   *
   * This is the only place widths are read back, because it is the only place the grid needs them: MUI holds a
   * resized width in its own column state and rebuilds that state from this array whenever its identity changes.
   * Resizing therefore does not run this memo — see changeColumnWidth.
   */
  const { gridColumns, queryVisibilityModel, defaultColumnWidths } = useMemo(() => {
    const { columns, hiddenFields } = expandSubColumns(getDispatchGridColDefs(currentQuery));
    /** Every column gets an entry, so one that declares no width of its own still has a width Reset can restore */
    const defaultWidths = new Map(columns.map(({ field, width }) => [field, width ?? fallbackColumnWidth]));

    return {
      gridColumns: applyColumnWidths(columns, storageKey),
      queryVisibilityModel: { __check__: true, ...hiddenFields },
      defaultColumnWidths: defaultWidths
    };
  }, [currentQuery, storageKey]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() =>
    applyHiddenColumns(queryVisibilityModel, storageKey)
  );
  const [seededKey, setSeededKey] = useState({ storageKey, queryVisibilityModel });

  if (seededKey.storageKey !== storageKey || seededKey.queryVisibilityModel !== queryVisibilityModel) {
    setSeededKey({ storageKey, queryVisibilityModel });
    setColumnVisibilityModel(applyHiddenColumns(queryVisibilityModel, storageKey));
  }

  const changeColumnVisibility = useCallback(
    (model: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(model);
      persistHiddenColumns(model, queryVisibilityModel, storageKey);
    },
    [queryVisibilityModel, storageKey]
  );

  /**
   * Deliberately persists without setting state: the grid has already applied the new width to its own column
   * state by the time this fires, so rebuilding the columns array here would re-render every row to arrive at the
   * layout already on screen. MUI clamps the drag to the definition's minWidth, so `width` is already in bounds.
   */
  const changeColumnWidth = useCallback(
    ({ colDef, width }: GridColumnResizeParams) => {
      const defaultWidth = defaultColumnWidths.get(colDef.field);
      /** Absent only for columns the grid adds itself, which carry no stored width to keep in step */
      if (defaultWidth !== undefined) {
        persistColumnWidth(colDef.field, width, defaultWidth, storageKey);
      }
    },
    [defaultColumnWidths, storageKey]
  );

  const resetWidths = useCallback(() => {
    resetColumnWidths(gridApiRef, defaultColumnWidths, storageKey);
  }, [gridApiRef, defaultColumnWidths, storageKey]);

  const slotProps = useMemo<DataGridProProps['slotProps']>(
    () => ({ columnsManagement: { onResetColumnWidths: resetWidths } }),
    [resetWidths]
  );

  /**
   *
   * @param params
   */
  const onCellClickHandler = useCallback(
    (params: GridCellParams<DispatchLeg>, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
      const extendedColDef: GridColDefExtended<DispatchLeg> = params.colDef;
      event.defaultMuiPrevented = true;
      if (extendedColDef.renderEditableCell && popoverRef.current) {
        let selectedRows = [params.row];

        if (extendedColDef.disableBatchEdit !== true) {
          /** Editing a cell selects its row so the clicked leg is always part of a batch save */
          gridApiRef.current?.selectRow(params.id, true);
          selectedRows = Array.from(gridRowSelectionIdsSelector(gridApiRef).values()) as DispatchLeg[];

          if (typeof extendedColDef.disableBatchEdit === 'function' && extendedColDef.disableBatchEdit(selectedRows)) {
            selectedRows = [params.row];
          }
        }

        const editableContent = extendedColDef.renderEditableCell(params.row, popoverRef.current.editableRef, selectedRows);
        if (editableContent) {
          popoverRef.current.open(event.currentTarget, editableContent, selectedRows.length);
        }
      }
    },
    [gridApiRef]
  );

  /** Memoized slots to prevent re-renders */
  const slots = useMemo<Partial<GridSlots>>(
    () => ({
      loadingOverlay: BrandLinearProgress,
      noRowsOverlay: () => <GenericOverlay overlayText='No Dispatch Legs Found.' />,
      noResultsOverlay: () => <GenericOverlay overlayText='No Dispatch Leg Results.' />,
      toolbar: DispatchGridToolbar,
      columnsManagement: ColumnsManagement
    }),
    []
  );

  /** Memoized sx styles to prevent re-renders */
  const sx = useMemo<DataGridProProps['sx']>(
    () => ({
      ...applyThemeGridSxOverrides(),
      // Performance optimizations for scroll rendering
      '& .MuiDataGrid-virtualScroller': {
        willChange: 'transform',
        contain: 'strict'
      },
      '& .MuiDataGrid-row': {
        contain: 'layout style paint'
      },
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'space-between'
      },
      '& .MuiDataGrid-row.Mui-selected': {
        backgroundColor: theme.palette.grey[400]
      },
      [`& .MuiDataGrid-row.Mui-selected .${styles['pinned-column']}`]: {
        backgroundColor: `${theme.palette.grey[400]} !important`
      },
      '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: `${theme.palette.grey[400]} !important`
      },
      '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
        backgroundColor: theme.palette.action.hover
      },
      '& .MuiDataGrid-row.Mui-selected:hover': {
        backgroundColor: theme.palette.action.hover
      },
      '& .MuiDataGrid-row.Mui-selected .row-menu-btn': {
        visibility: 'visible'
      },
      '& .MuiDataGrid-row:hover .row-menu-btn': {
        visibility: 'visible'
      },
      '& .MuiDataGrid-row:hover': {
        backgroundColor: theme.palette.action.hover
      },
      '& .editable-cursor:hover': {
        outline: `1px dashed ${theme.palette.primary.main}`,
        outlineOffset: '-2px',
        backgroundColor: theme.palette.action.selected
      }
    }),
    [theme.palette.action.hover, theme.palette.primary.main, theme.palette.action.selected, theme.palette.grey]
  );

  return (
    <>
      <DataGridPro
        apiRef={gridApiRef}
        rows={dispatchLegs}
        rowHeight={50}
        columns={gridColumns}
        pinnedColumns={currentQuery.configuration.pinnedCols}
        columnHeaderHeight={50}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        checkboxSelection
        filterModel={filterModel}
        onFilterModelChange={changeFilterModel}
        sortModel={sortingModel}
        onSortModelChange={changeSortModel}
        onCellClick={onCellClickHandler}
        loading={isLoading}
        slots={slots}
        slotProps={slotProps}
        sx={sx}
        initialState={initialGridState}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={changeColumnVisibility}
        onColumnWidthChange={changeColumnWidth}
        /** Opts out of the app-wide MuiDataGrid default that turns resizing off (see AppThemeOverrides) */
        disableColumnResize={false}
        /**
         * Separator double-click autosizes from the rendered rows only, discarding outliers, so it fits one
         * screenful rather than the dataset — not a width to persist for every leg the query returns.
         */
        disableAutosize
        disableRowSelectionOnClick
      />
      <EditableCellPopover ref={popoverRef} />
    </>
  );
}
